import { useHealthCheck } from 'layout/context/healthCheckContext'
import { useToast } from 'layout/context/toast-context'
import { Button } from 'primereact/button'
import { useState } from 'react'
import healthApiService from '../api/health.api'
import './health-view.scss'
import { useAuth } from 'layout/context/auth-context'
import { useSignalR } from 'layout/context/signalr-context'
import { HaioHttpResponse } from '../types/haio-http-response'
import { AxiosError } from 'axios'
import { buildDemoAlarm } from '../utils/demo-alarm'
import * as signalR from '@microsoft/signalr'

const HealthView = () => {
  const { healthStatus } = useHealthCheck()
  const { clientId } = useAuth()
  const { showSuccess, showError } = useToast()
  const { connection } = useSignalR()
  const [isProcessing, setIsProcessing] = useState(false)

  const getHealthStatusDisplay = () => {
    if (healthStatus.isLoading) {
      return { text: 'Verificando...', className: 'health-checking' }
    }

    if (healthStatus.isHealthy) {
      return { text: 'Saudável', className: 'health-healthy' }
    }

    return { text: 'Não saudável', className: 'health-unhealthy' }
  }

  const getHealthTooltip = () => {
    if (healthStatus.lastChecked) {
      const lastCheck = new Intl.DateTimeFormat('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(healthStatus.lastChecked)

      return `Última verificação: ${lastCheck}${healthStatus.error ? ` - Erro: ${healthStatus.error}` : ''
        }`
    }

    return 'Verificação de saúde do sistema'
  }

  const handleRestartStream = async () => {
    setIsProcessing(true)
    await healthApiService.restartStream()
      .then((data: HaioHttpResponse) => {
        showSuccess(data.status, data.message)
      })
      .catch((error: AxiosError) => {
        const response = error.response?.data as HaioHttpResponse | undefined
        if (response?.status && response?.message) {
          showError(response.status, response.message)
        } else {
          showError('Erro de conexão', 'Serviço temporariamente indisponível')
        }
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  const handleStopStream = async () => {
    setIsProcessing(true)
    await healthApiService.stopStream()
      .then((data: HaioHttpResponse) => {
        showSuccess(data.status, data.message)
      })
      .catch((error: AxiosError) => {
        const response = error.response?.data as HaioHttpResponse | undefined
        if (response?.status && response?.message) {
          showError(response.status, response.message)
        } else {
          showError('Erro de conexão', 'Serviço temporariamente indisponível')
        }
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  const handleCleanCache = async () => {
    setIsProcessing(true)
    if (clientId) {
      await healthApiService.clearCache(clientId)
        .then((data: HaioHttpResponse) => {
          showSuccess(data.status, data.message)
        })
        .catch((error: AxiosError) => {
          const response = error.response?.data as HaioHttpResponse
          showError(response.status, response.message)
        }).finally(() => {
          setIsProcessing(false)
        })
    } else {
      showError('Erro', 'Client ID não encontado')
    }
  }
  const handleStartStream = async () => {
    setIsProcessing(true)
    await healthApiService.startStream()
      .then((data: HaioHttpResponse) => {
        showSuccess(data.status, data.message)
      })
      .catch((error: AxiosError) => {
        const response = error.response?.data as HaioHttpResponse | undefined
        if (response?.status && response?.message) {
          showError(response.status, response.message)
        } else {
          showError('Erro de conexão', 'Serviço temporariamente indisponível')
        }
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  const handleSimulateAlarm = async () => {
    // Checked against the connection's own state rather than the `connected`
    // flag from context: that flag is only set once the .then() callback of
    // .start() runs, so it can still read stale/false for one render right
    // after the connection actually finishes negotiating.
    if (!connection || connection.state !== signalR.HubConnectionState.Connected) {
      showError('Indisponível', 'Sem conexão com o servidor de eventos')
      return
    }

    setIsProcessing(true)
    await connection
      .invoke('BroadcastAlarmEvent', buildDemoAlarm())
      .catch(() => {
        showError('Erro', 'Não foi possível emitir o alarme de teste')
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  const healthDisplay = getHealthStatusDisplay()

  if (healthStatus.isLoading) {
    return (
      <div className='health-container'>
        <div className='flex flex-column align-items-center justify-content-center min-h-screen p-4'>
          <h1 className='text-center mb-5'>Verificação de Saúde do Sistema</h1>
          <div className='mt-4'>
            <div className='health-spinner'></div>
            <div className='text-center mt-3 text-lg text-600'>
              Verificando saúde do sistema...
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='health-container'>
      <div className='flex flex-column align-items-center justify-content-center min-h-screen p-4'>
        <h1 className='text-center mb-4'>Verificação de Saúde do Sistema</h1>

        {/* Action Buttons */}
        <div className='flex flex-wrap gap-3 mb-5 justify-content-center'>
          <Button
            label='Iniciar Stream'
            icon='pi pi-play'
            severity='success'
            onClick={handleStartStream}
            disabled={isProcessing}
            loading={isProcessing}
          />
          <Button
            label='Reiniciar Stream'
            icon='pi pi-refresh'
            severity='warning'
            onClick={handleRestartStream}
            disabled={isProcessing}
            loading={isProcessing}
          />
          <Button
            label='Parar Stream'
            icon='pi pi-stop'
            severity='danger'
            onClick={handleStopStream}
            disabled={isProcessing}
            loading={isProcessing}
          />
        </div>
        <div className='flex flex-wrap gap-3 justify-content-center'>
          <Button
            label='Limpar cache'
            icon='pi pi-eraser'
            severity='info'
            onClick={handleCleanCache}
            disabled={isProcessing}
            loading={isProcessing}
          />
          <Button
            label='Emitir alarme de teste'
            icon='pi pi-bell'
            severity='help'
            onClick={handleSimulateAlarm}
            disabled={isProcessing}
            loading={isProcessing}
          />
        </div>

        {/* Health Status Card */}
        <div
          className='health-status-card p-4 border-round-lg mb-3 mt-2'
          style={{
            backgroundColor: healthStatus.isHealthy ? '#d4edda' : '#f8d7da',
            color: healthStatus.isHealthy ? '#155724' : '#721c24',
            border: `1px solid ${healthStatus.isHealthy ? '#c3e6cb' : '#f5c6cb'
              }`,
          }}
          title={getHealthTooltip()}
        >
          {healthDisplay.text}
        </div>

        {healthStatus.lastChecked && (
          <div className='text-sm text-600 mb-2'>{getHealthTooltip()}</div>
        )}

        {healthStatus.error && (
          <div className='text-sm mb-3' style={{ color: '#dc3545' }}>
            Erro: {healthStatus.error}
          </div>
        )}

        {/* Service Details */}
        {healthStatus.checks && healthStatus.checks.length > 0 && (
          <div className='w-full mt-5' style={{ maxWidth: '1200px' }}>
            <h2 className='text-xl mb-4 text-center'>Detalhes dos Serviços</h2>
            <div className='grid'>
              {healthStatus.checks.map((check, index) => (
                <div key={index} className='col-12 md:col-6 lg:col-4'>
                  <div
                    className='health-check-card p-4 border-round-lg h-full flex flex-column'
                    style={{
                      border: `2px solid ${check.status === 'Healthy' ? '#4caf50' : '#f44336'
                        }`,
                      backgroundColor:
                        check.status === 'Healthy' ? '#1b5e20' : '#b71c1c',
                      minHeight: '280px',
                    }}
                  >
                    <div className='flex justify-content-between align-items-center mb-3'>
                      <h3 className='m-0 text-base font-bold' style={{ color: '#ffffff' }}>
                        {check.service}
                      </h3>
                      <span
                        className='px-2 py-1 border-round-3xl text-xs font-bold'
                        style={{
                          backgroundColor:
                            check.status === 'Healthy' ? '#4caf50' : '#f44336',
                          color: '#ffffff',
                        }}
                      >
                        {check.status}
                      </span>
                    </div>

                    <div className='text-xs mb-3' style={{ color: '#e0e0e0' }}>
                      <div className='mb-1'>
                        <i className='pi pi-clock mr-2' />
                        {check.duration}
                      </div>
                      <div>
                        <i className='pi pi-calendar mr-2' />
                        {new Date(check.timestamp).toLocaleString('pt-BR')}
                      </div>
                    </div>

                    {check.data && (
                      <div className='text-xs flex-1' style={{ color: '#f5f5f5' }}>
                        <strong>Dados:</strong>
                        <pre className='health-data-pre mt-2 p-2 border-round overflow-auto text-xs' style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.3)',
                          color: '#f5f5f5'
                        }}>
                          {JSON.stringify(check.data, null, 2)}
                        </pre>
                      </div>
                    )}

                    {check.error && (
                      <div className='mt-2 text-xs font-semibold' style={{ color: '#ffcdd2' }}>
                        <strong>Erro:</strong> {check.error}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HealthView
